import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import JourneyStepper from '../components/JourneyStepper';
import { CustomerInfoCard } from './ReviewOrder/CustomerInfoCard';
import { OrderItemsCard } from './ReviewOrder/OrderItemsCard';
import { ProductionPriorityCard } from './ReviewOrder/ProductionPriorityCard';
import { StickerPreviewCard } from './ReviewOrder/StickerPreviewCard';
import { CostSummaryCard } from './ReviewOrder/CostSummaryCard';
import { WhatHappensNext } from './ReviewOrder/WhatHappensNext';
import { ReviewOrderError } from './ReviewOrder/ReviewOrderError';
import { ProofItemPicker } from './ReviewOrder/ProofItemPicker';
import { ShippingMethodCard } from './ReviewOrder/ShippingMethodCard';
import { useShippingRates } from '../hooks/useShippingRates';
import InlineProductionSizeAlert from './UploadArtwork/InlineProductionSizeAlert';
import { log } from '../lib/logger';
import { taxBreakdown } from '../lib/taxCalculator';
import { countryToIso } from './ReviewOrder/utils';
import { usePreviewVersions } from './ReviewOrder/usePreviewVersions';
import { useReviewSubmit } from './ReviewOrder/useReviewSubmit';
import { PlaceOrderLoadingOverlay } from '../components/PlaceOrderLoadingOverlay';
import {
  clearActiveDraftOrderId,
  setActiveDraftOrderId,
} from '../lib/activeDraftOrder';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewOrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId: orderIdParam } = useParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [orderId, setOrderId] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [specialInstructions, setSpecialInstructions] = useState('');
  // Discount-code state (iter105) — applied/removed via /api/discounts/*.
  // Must be declared at the top of the component (before any early
  // returns) to satisfy React's rules-of-hooks.
  const [discountBusy, setDiscountBusy] = useState(false);
  const [discountError, setDiscountError] = useState(null);
  // Production-size adjustment info (for orders with custom cutlines whose
  // artwork forced a proportional downscale). Passively displayed as a
  // banner — no action required from the customer.
  const [productionSizeInfo, setProductionSizeInfo] = useState(null);

  // Which item is the customer currently previewing? Null = order-level
  // (legacy single-item flow). Auto-picked to the first item on order load.
  const [activeItemId, setActiveItemId] = useState(null);

  // Preview My Sticker state — versions array + load/clear actions.
  // Encapsulated in usePreviewVersions so this page stays readable.
  const {
    previewVersions,
    activeVersion,
    setActiveVersion,
    previewLoading,
    previewError,
    setPreviewError,
    loadPreview,
    clearVersions,
  } = usePreviewVersions({
    orderId,
    activeItemId,
    order,
  });

  // Convenience selectors for the currently-visible version
  const activePreview = previewVersions[activeVersion] || null;
  const previewUrl = activePreview?.previewUrl || null;
  const cleanUrl = activePreview?.cleanUrl || null;

  // Hover-to-reveal clean sticker
  const [hovering, setHovering] = useState(false);

  // Shipping rate state (carried over from Customer Info page, then refreshed here)
  const [selectedRate, setSelectedRate] = useState(location.state?.selectedShippingRate || null);
  const shippingRates = useShippingRates();

  // iter114s.90 — Auto-select the cheapest live rate the moment rates
  // arrive (and no rate is already selected). The hook returns
  // `top_n=3` already sorted cheapest-first, so `rates[0]` is the right
  // pick. Without this, the cart sidebar silently displays $0 shipping
  // + $0 tax until the customer clicks a radio — which most don't,
  // because the rates render *below* the cost summary and look like
  // "informational" options rather than required input. Customers
  // were placing Net-30 orders for "Total $264" when the real total
  // (with shipping + HST) is closer to $295. Auto-selection makes the
  // cheapest visible rate authoritative; the customer can still pick
  // a faster service manually.
  useEffect(() => {
    if (selectedRate) return;
    const rates = shippingRates?.rates || [];
    if (rates.length > 0) {
      setSelectedRate(rates[0]);
    }
  }, [shippingRates?.rates, selectedRate]);

  // Re-fetch rates on mount so the final pricing is locked against current live data
  useEffect(() => {
    if (!order?.items?.length) return;
    const info = order.shipping_info || {};
    if (!info.postal_code || !info.city) return;
    const country = countryToIso(info.country);
    shippingRates.fetchRates({
      shipTo: {
        street: info.address_line1 || 'Address',
        city: info.city,
        province: info.state_province || '',
        postal_code: info.postal_code,
        country,
        residential: true,
      },
      items: order.items,
    }).catch(() => {/* handled in hook */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  useEffect(() => {
    // Wait for AuthContext to finish hydrating — direct deep-links
    // (e.g. /review-order/<id> from a customer's bookmark) would
    // otherwise see isAuthenticated=false for one frame and bounce
    // to /login before the cookie-backed /me call resolves.
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Resolve orderId from THREE sources, in priority order:
    //   1. /review-order/:orderId path param (deep-linkable, refresh-safe)
    //   2. router state (the in-app navigation flow from /quote)
    //   3. nothing → bounce back to /quote
    const nextOrderId = orderIdParam || location.state?.orderId;
    if (nextOrderId) {
      setOrderId((prev) => {
        if (prev && prev !== nextOrderId) clearVersions();
        return nextOrderId;
      });
      fetchOrder(nextOrderId);
    } else {
      navigate('/quote');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, orderIdParam, location, navigate, retryTick]);

  const fetchOrder = async (id) => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await axios.get(
        `${API_URL}/api/enhanced-orders/${id}`,

      );
      if (!response.data || !response.data.id) {
        // Defensive: empty/mangled response still needs a user-visible
        // state rather than silently falling through to "Order not found".
        throw new Error('Empty order response');
      }
      setOrder(response.data);
      // Seed the "Special Instructions" textarea from the server-side
      // draft notes the customer typed on Page 2 (only on first load —
      // don't stomp over any text they've already begun editing here).
      const draftNotes = (response.data?.artwork_instructions_draft || '').trim();
      if (draftNotes) {
        setSpecialInstructions((prev) => (prev.trim() ? prev : draftNotes));
      }
      // Sync the persistent draft pointer with the fetched order's
      // status: keep it pointing at this order while it's still a draft
      // (so future "Add new item" clicks append correctly), but clear it
      // the moment the order has progressed beyond draft (proof_pending,
      // submitted, etc.) so a stale id can't push items into a locked
      // order.
      const status = (response.data?.status || '').toLowerCase();
      if (status === 'draft') {
        setActiveDraftOrderId(response.data.id);
      } else if (status) {
        clearActiveDraftOrderId();
      }
      // Auto-select the first item for preview scope. Single-item orders
      // still get an activeItemId set, but the backend falls back to
      // order-level artwork when the item has none bound — so nothing
      // regresses for legacy orders.
      const firstItem = response.data?.items?.[0];
      if (firstItem?.id) setActiveItemId(firstItem.id);

      // Passively fetch the production-size estimate for custom-cutline
      // orders whose artwork forces a proportional scale. Renders as an
      // informational banner above the order items card — no action needed.
      const isCustom = (response.data?.cutline_shape || '').toLowerCase() === 'custom';
      if (isCustom) {
        try {
          const est = await axios.get(
            `${API_URL}/api/enhanced-orders/${id}/production-size-estimate`
          );
          // Show the "cut-line area" chip whenever the estimate is
          // applicable — even when `was_adjusted=false` (i.e. the item
          // was already scaled earlier in the flow). Customers revisiting
          // the Review page still deserve to see the actual cut area +
          // material-savings win they secured.
          if (est.data?.applicable) {
            // Normalize backend field names to the shape InlineProductionSizeAlert expects.
            setProductionSizeInfo({
              production_width: est.data.prod_w_in,
              production_height: est.data.prod_h_in,
              requested_width: Number(firstItem?.width) || null,
              requested_height: Number(firstItem?.height) || null,
              scale_factor: est.data.scale_factor,
            });
          } else {
            setProductionSizeInfo(null);
          }
        } catch (e) {
          log.warn('Production-size estimate fetch failed:', e);
          setProductionSizeInfo(null);
        }
      } else {
        setProductionSizeInfo(null);
      }
    } catch (error) {
      log.error('Failed to fetch order:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (status === 404) {
        setFetchError({ kind: 'not-found', message: detail || 'We could not find this order.' });
      } else if (status === 401 || status === 403) {
        setFetchError({ kind: 'auth', message: 'Your session expired. Please sign in again.' });
      } else if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
        setFetchError({ kind: 'timeout', message: 'The server took too long to respond. Please retry.' });
      } else {
        setFetchError({ kind: 'network', message: detail || error?.message || 'Network error — please retry.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // When the customer switches which item they're previewing, clear the
  // version list — versions are scoped per-item so history doesn't bleed.
  const switchActiveItem = (itemId) => {
    if (itemId === activeItemId) return;
    clearVersions();
    setActiveItemId(itemId);
  };

  // Artwork presence — either order-level (legacy) OR the active item has
  // its own artwork_file_ids (multi-item path).
  const activeItem = (order?.items || []).find((it) => it.id === activeItemId);
  const hasArtwork =
    (order?.artwork_files?.length || 0) > 0 ||
    (activeItem?.artwork_file_ids?.length || 0) > 0;

  const probe = `${(order?.items?.[0]?.material || '').toLowerCase()} ${(order?.items?.[0]?.product_type || '').toLowerCase()}`;
  const isFoilProduct = probe.includes('foil') || probe.includes('gold & silver') || probe.includes('gold-silver');

  const handleFoilChange = async ({ foilType, foilColorHex }) => {
    // Optimistically update local state so UI reflects choice immediately
    setOrder(prev => prev ? { ...prev, foil_type: foilType, foil_color_hex: foilColorHex } : prev);
    try {
      const qs = foilType === 'colored' && foilColorHex
        ? `foil_type=colored&foil_color_hex=${encodeURIComponent(foilColorHex)}`
        : `foil_type=${encodeURIComponent(foilType)}`;
      await axios.patch(`${API_URL}/api/enhanced-orders/${orderId}/foil-options?${qs}`);
      // If a preview is already rendered, auto-refresh it to reflect the new foil choice
      if (previewUrl) await loadPreview();
    } catch (e) {
      setPreviewError('Could not save foil choice. Please try again.');
    }
  };

  /**
   * iter114f: per-item delete for multi-item orders. Customer asked for
   * the ability to drop an item when they only want to check out a
   * subset. We re-use the existing
   * DELETE /enhanced-orders/{orderId}/remove-item/{itemId} endpoint
   * (already used by OrderSummaryPage), then re-fetch the order so
   * pricing / shipping / proof-picker all stay in sync. We also reset
   * the active item if the customer just removed the one they were
   * previewing.
   */
  const handleRemoveItem = async (itemId) => {
    if (!orderId || !itemId) return;
    const ok = window.confirm(
      'Remove this item from the order? Other items in the cart will be kept and you can continue to checkout with them.',
    );
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/enhanced-orders/${orderId}/remove-item/${itemId}`);
      if (activeItemId === itemId) setActiveItemId(null);
      await fetchOrder(orderId);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to remove item.';
      log.error('Item remove failed:', err);
      alert(detail);
    }
  };

  // Place Order / Approve Proof & Submit — bundled Freightcom save +
  // /submit POST + navigate encapsulated in useReviewSubmit. Both the
  // right-side REQUEST ORDER button AND the bottom "Approve Proof &
  // Submit Order" button funnel through here so the payment gate
  // applies uniformly to every submit path.
  const { submitting, handleSubmitOrder } = useReviewSubmit({
    orderId,
    order,
    selectedRate,
    shippingRates,
    specialInstructions,
    navigate,
    billingMode: user?.billing_mode || 'pay_now',
    paymentStatus: order?.payment_status || '',
    isAdmin: !!user?.is_admin,
  });

  // Resolve the active currency from (1) the order document (set at
  // create-draft, defaults to CAD) falling back to (2) the logged-in
  // user's country. The previous implementation hard-coded USD which
  // was misleading for Canadian customers since the entire pricing
  // engine stores values in CAD and only converts for the FX toggle.
  const resolveActiveCurrency = () => {
    const orderCurrency = (order?.currency || '').toUpperCase();
    if (orderCurrency === 'CAD' || orderCurrency === 'USD') return orderCurrency;
    return user?.country === 'Canada' ? 'CAD' : 'USD';
  };
  const activeCurrency = resolveActiveCurrency();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(activeCurrency === 'CAD' ? 'en-CA' : 'en-US', {
      style: 'currency',
      currency: activeCurrency,
    }).format(amount);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-testid="review-order-loading">
        {/* Branded full-screen overlay — same animation the customer
            saw on the Place Order click. Avoids the jarring "Loading…"
            text dump that previously made the wait feel broken. The
            rotating reassuring messages cycle every 1.6s so even a
            slow-network 5s GET sees 3 different lines. */}
        <PlaceOrderLoadingOverlay
          open
          headline="Hang tight — opening your review page."
        />
      </div>
    );
  }

  if (!order) {
    return (
      <ReviewOrderError
        fetchError={fetchError}
        onRetry={() => setRetryTick((t) => t + 1)}
      />
    );
  }

  const rawSubtotal = order.original_subtotal ?? order.total_price ?? 0;
  const discountCode = order.discount_code || null;
  const discountAmount = order.discount_amount || 0;
  const discountPercent = order.discount_percent || 0;
  const effectiveSubtotal = Math.max(rawSubtotal - discountAmount, 0);
  const subtotal = rawSubtotal;
  // iter108b: shipping is now `null` (not 4.99) when the customer
  // hasn't picked a Freightcom rate yet, so the cost summary +
  // PDF + Pay button all consistently treat that case as "TBD".
  // The previous $4.99 fallback charged customers an arbitrary amount
  // that didn't match the PDF — pointed out as an iter58 minor.
  const shipping = selectedRate?.total_price ?? null;
  const shippingKnown = shipping !== null && shipping !== undefined;

  // iter108: tax auto-calculated from the ship-to country/state using
  // the same rate table the backend Quote / Order Confirmation PDFs
  // use, so the cost summary the customer pays reconciles to the
  // dollar with what's printed on the PDF. US = exempt; CA = provincial
  // GST/HST per the user's reference chart; unknown ship-tos surface
  // "Tax TBD" so the customer never silently pays $0 by accident.
  const shippingInfo = order.shipping_info || {};
  const taxCountry =
    order.freightcom_quote?.ship_to?.country
    || shippingInfo.country
    || '';
  const taxState =
    order.freightcom_quote?.ship_to?.region
    || shippingInfo.state_province
    || shippingInfo.state
    || '';
  // Tax is computed against the discounted items + shipping. When
  // shipping is unknown we still compute tax on items alone — giving
  // the customer a partial, accurate figure rather than nothing.
  const taxInfo = taxBreakdown(effectiveSubtotal, shippingKnown ? shipping : 0, taxCountry, taxState);
  const tax = taxInfo.taxAmount;
  const taxLabel = taxInfo.label;
  // iter114s.40 — Total now resolves even before a shipping rate is
  // picked: it falls back to subtotal + tax with shipping treated as
  // $0.00. The footnote ("* estimated shipping costs are subject to
  // change upon order confirmation") covers the disclaimer, and the
  // Place Order button stays enabled so Net-30 customers can submit
  // without a fully-bound Freightcom quote.
  const total = effectiveSubtotal + (shippingKnown ? shipping : 0) + (tax || 0);

  // Discount apply/remove handlers — both refetch the order on success
  // so subtotal/discount/total all redraw atomically. State is declared
  // at the top of the component (above) to honour rules-of-hooks.
  const handleApplyDiscount = async (rawCode) => {
    const code = (rawCode || '').trim();
    if (!code || !orderId) return;
    setDiscountError(null);
    setDiscountBusy(true);
    try {
      await axios.post(`${API_URL}/api/discounts/apply`, { order_id: orderId, code });
      await fetchOrder(orderId);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not apply that code. Try again or check the spelling.';
      setDiscountError(detail);
      log.warn('[review] discount apply failed', err?.response?.status, detail);
    } finally {
      setDiscountBusy(false);
    }
  };
  const handleRemoveDiscount = async () => {
    if (!orderId) return;
    setDiscountError(null);
    setDiscountBusy(true);
    try {
      await axios.post(`${API_URL}/api/discounts/remove`, { order_id: orderId });
      await fetchOrder(orderId);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not remove the discount. Try again.';
      setDiscountError(detail);
      log.warn('[review] discount remove failed', err?.response?.status, detail);
    } finally {
      setDiscountBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Full-page submit overlay — same branded animation as Place
          Order with copy tailored for the longer Submit step (Freightcom
          quote save + proof/quote/invoice PDF regeneration + Converge
          auth call). Prevents customers from refreshing/back-buttoning
          mid-charge, which previously caused duplicate-charge support
          tickets. */}
      <PlaceOrderLoadingOverlay
        open={submitting}
        headline="Your order is locked in — preparing your proof."
        // Submit-phase messages: leans into the "we're locking in your
        // order" framing so the customer feels the conversion finish
        // line is crossed even before navigation. Same knight loop
        // rendering as the place-order overlay anchors the brand.
        messages={[
          'Locking in your order — almost there.',
          'Confirming your shipping rate…',
          'Generating your proof, quote, and invoice…',
          'Wrapping up — your confirmation is on its way.',
        ]}
      />

      {/* iter114s.45 — Per-page brand band removed; unified
          JourneyStepper handles all the chrome. ReviewOrder is the
          final pre-submit step so it sits inside the "Checkout"
          stage (4) alongside CustomerInfoPage. */}
      <JourneyStepper currentStage={4} orderId={orderId} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Review Order</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Production-size adjustment — passive warning shown when a
                custom cutline's detected die-line forces a proportional scale */}
            {productionSizeInfo && (
              <InlineProductionSizeAlert info={productionSizeInfo} />
            )}

            {/* Multi-item: picker to select which item's proof to preview */}
            <ProofItemPicker
              items={order.items}
              activeItemId={activeItemId}
              onSelectItem={switchActiveItem}
            />

            {/* Preview My Sticker — hoisted to the top of the column and
                auto-opens on mount (see autoLoad) so customers land on a
                proof preview, not a form. Biggest visual signal that
                their order looks right before the submit CTA. */}
            <StickerPreviewCard
              order={order}
              hasArtwork={hasArtwork}
              isFoilProduct={isFoilProduct}
              submitting={submitting}
              previewUrl={previewUrl}
              previewLoading={previewLoading}
              previewError={previewError}
              cleanUrl={cleanUrl}
              hovering={hovering}
              previewVersions={previewVersions}
              activeVersion={activeVersion}
              onSelectVersion={setActiveVersion}
              onLoadPreview={loadPreview}
              onSetHovering={setHovering}
              onFoilChange={handleFoilChange}
              onApproveSubmit={() => handleSubmitOrder({ proofApproved: true })}
              onRequestManualProof={() => handleSubmitOrder({ proofApproved: false })}
              onUploadArtworkClick={() => navigate('/upload-artwork', {
                state: {
                  orderId,
                  orderNumber: order?.order_number,
                  itemId: activeItemId,
                  shippingAddress: order?.shipping_info || {},
                  country: order?.shipping_info?.country,
                  returnTo: '/review-order',
                },
              })}
              billingMode={user?.billing_mode || 'pay_now'}
              paymentStatus={order?.payment_status || ''}
              isAdmin={!!user?.is_admin}
              formattedTotal={formatCurrency(order?.total_price || 0)}
              autoLoad
            />

            {/* Order Items */}
            <OrderItemsCard
              items={order.items || []}
              formatCurrency={formatCurrency}
              onAddItem={() => navigate('/quote', { state: { orderId, addToExisting: true } })}
              onRemoveItem={handleRemoveItem}
            />

            {/* Production Priority (Phase 3) — customer self-declares
                rush + reseller-verification requests. Backend's admin
                cockpit still owns the actual reseller_tier assignment
                (see /api/admin/orders/{id}/quick-attrs). */}
            <ProductionPriorityCard orderId={orderId} order={order} />

            {/* Customer Information */}
            <CustomerInfoCard shippingInfo={shippingInfo} userProfile={user} />

            {/* Live shipping rate — locked to order on submit */}
            <ShippingMethodCard
              orderNumber={order.order_number}
              shippingRates={shippingRates}
              selectedRate={selectedRate}
              setSelectedRate={setSelectedRate}
            />
          </div>

          {/* Right: Cost Summary & Special Instructions
              iter114s.40 — Sticky scroll: as the customer scrolls
              through the long Preview / Order Items / Customer Info /
              Shipping Method stack on the left, the cost summary on
              the right follows along (top:5rem clears the global
              TopNav, self-start prevents the grid from stretching
              the sticky container to the row's full height). */}
          <div className="lg:col-span-1 space-y-6 lg:self-start lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            <CostSummaryCard
              itemCount={order.items?.length || 0}
              subtotal={subtotal}
              shipping={shipping}
              tax={tax}
              taxLabel={taxLabel}
              total={total}
              formatCurrency={formatCurrency}
              currencyCode={activeCurrency}
              specialInstructions={specialInstructions}
              onChangeSpecialInstructions={(e) => setSpecialInstructions(e.target.value)}
              submitting={submitting}
              onSubmit={() => handleSubmitOrder({ proofApproved: false })}
              orderId={orderId}
              paymentStatus={order?.payment_status}
              onPaymentAuthorized={() => fetchOrder(orderId)}
              billingMode={user?.billing_mode || 'pay_now'}
              isAdmin={!!user?.is_admin}
              discountCode={discountCode}
              discountAmount={discountAmount}
              discountPercent={discountPercent}
              discountBusy={discountBusy}
              discountError={discountError}
              onApplyDiscount={handleApplyDiscount}
              onRemoveDiscount={handleRemoveDiscount}
            />
            {/* iter114s.45 — confidence-building post-submit timeline.
                Lives directly under the cost summary so the customer's
                last view before clicking Place Order is "here's what
                will actually happen to my order" — reduces support
                tickets + abandonment per the operator's UX brief. */}
            <WhatHappensNext hasArtwork={hasArtwork} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewOrderPage;
